(() => {
  // Continuous marquee slider that freezes on hover.
  // The same track is a CSS grid in grid mode; Swiper is only initialized when the *current*
  // breakpoint's layout is "slider" (desktop >= 750px uses data-desktop-layout, below uses
  // data-mobile-layout) and destroyed otherwise, letting the CSS grid take over.

  const DESKTOP_BP = 750; // layout boundary + first column step (matches the SCSS breakpoints)
  const PEEK = 0.15; // fraction of the next card always visible in slider mode
  const SPACE_BETWEEN = 0; // px, no gap between slides (per the mockup)
  const INTERACTION_SPEED = 1200; // ms, speed for drag / wheel while the marquee is paused
  const CURSOR_EASE = 0.2; // trailing weight for the drag-hint cursor
  const CURSOR_IDLE_MS = 600; // hide the drag-hint after the pointer stops moving

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  const pointerFine = window.matchMedia("(pointer: fine)").matches;

  // Swiper instances keyed by the slider element, and a cache of each track's original slides
  // (so re-inits don't compound loop clones and switching back to grid restores a clean DOM).
  // The theme editor re-runs this script on every `shopify:section:load` (block add/remove/
  // reorder re-renders the section), so this state MUST be a single global singleton. Per-
  // execution WeakMaps would let a stale listener from an earlier run and the fresh run each
  // init Swiper on the same element (double-init → broken width until the merchant hits Save).
  if (!window.__featuredProducts) {
    window.__featuredProducts = {
      instances: new WeakMap(),
      originalSlides: new WeakMap(),
    };
  }
  const { instances, originalSlides } = window.__featuredProducts;

  const getOriginals = (wrapper) => {
    if (!originalSlides.has(wrapper)) {
      originalSlides.set(
        wrapper,
        Array.from(wrapper.children).map((node) => node.cloneNode(true))
      );
    }
    return originalSlides.get(wrapper);
  };

  const restoreOriginals = (wrapper) => {
    const originals = getOriginals(wrapper);
    wrapper.replaceChildren(...originals.map((node) => node.cloneNode(true)));
  };

  // Rebuild the track from the cached originals; for loop mode clone them until there are clearly
  // more slides than fit on screen (user's rule: at least cards_per_row + 3, and enough for Swiper
  // to loop cleanly).
  const buildSlides = (wrapper, isLoop, cardsPerRow) => {
    restoreOriginals(wrapper);
    if (!isLoop) return;

    const base = Array.from(wrapper.children).map((node) =>
      node.cloneNode(true)
    );
    if (base.length === 0) return;

    const minSlides = Math.max(cardsPerRow + 3, (cardsPerRow + 1) * 2);
    let guard = 0;
    while (wrapper.children.length < minSlides && guard < 12) {
      base.forEach((node) => wrapper.appendChild(node.cloneNode(true)));
      guard += 1;
    }
  };

  const buildBreakpoints = (cardsPerRow) => ({
    0: { slidesPerView: 1 + PEEK, spaceBetween: SPACE_BETWEEN },
    [DESKTOP_BP]: {
      slidesPerView: Math.min(cardsPerRow, 2) + PEEK,
      spaceBetween: SPACE_BETWEEN,
    },
    1200: {
      slidesPerView: Math.min(cardsPerRow, 3) + PEEK,
      spaceBetween: SPACE_BETWEEN,
    },
    1536: {
      slidesPerView: Math.min(cardsPerRow, 4) + PEEK,
      spaceBetween: SPACE_BETWEEN,
    },
  });

  const getActiveLayout = (root) => {
    return window.innerWidth < DESKTOP_BP
      ? root.dataset.mobileLayout
      : root.dataset.desktopLayout;
  };

  // Pause/resume of the marquee on hover. Freezes the belt in place under the pointer and, on
  // leave, finishes the interrupted step toward the original target at the constant marquee speed
  // (proportional duration -> no positional jump). Drag / wheel while paused use a snappy speed.
  const attachMarqueeControls = (swiper, marqueeSpeed) => {
    let savedTransform = null;
    let savedTargetTranslate = null;
    let pausedTranslate = null;
    let wasAutoplayRunning = false;
    let wasTouched = false;

    const getSlideWidth = () => swiper.slides[0]?.offsetWidth || 0;
    const getMovementSpeed = () => getSlideWidth() / swiper.params.speed;

    const mouseEnterHandler = () => {
      if (wasTouched || !swiper.autoplay) return;

      wasAutoplayRunning = swiper.autoplay.running;

      if (wasAutoplayRunning) {
        swiper.autoplay.stop();

        savedTransform = swiper.wrapperEl.style.transform;
        const targetMatch = savedTransform.match(/translate3d\(([^,]+)px/);
        savedTargetTranslate = targetMatch
          ? parseFloat(targetMatch[1])
          : swiper.getTranslate();

        pausedTranslate = swiper.getTranslate();

        swiper.wrapperEl.style.transitionDuration = "0ms";
        swiper.wrapperEl.style.transform = `translate3d(${pausedTranslate}px, 0px, 0px)`;

        swiper.params.speed = INTERACTION_SPEED;
        swiper.wrapperEl.style.transitionTimingFunction = "ease";
      }
    };

    const mouseLeaveHandler = () => {
      if (!wasAutoplayRunning || wasTouched || !swiper.autoplay) return;

      swiper.params.speed = marqueeSpeed;
      swiper.wrapperEl.style.transitionTimingFunction = "linear";

      const remainingDistance = Math.abs(
        savedTargetTranslate - pausedTranslate
      );
      const proportionalDuration = remainingDistance / getMovementSpeed();

      swiper.wrapperEl.style.transitionDuration = proportionalDuration + "ms";
      swiper.wrapperEl.style.transform = savedTransform;

      setTimeout(() => {
        if (swiper.autoplay && !swiper.destroyed) {
          swiper.autoplay.start();
        }
      }, 50);

      wasAutoplayRunning = false;
    };

    const touchStartHandler = () => {
      wasTouched = true;
      wasAutoplayRunning = false;
    };

    const touchEndHandler = () => {
      setTimeout(() => {
        wasTouched = false;
      }, 300);
    };

    // Keyboard: pause the belt while focus is inside the slider (WCAG 2.2.2 — a stop mechanism for
    // auto-moving content), resume once focus leaves it.
    const focusInHandler = () => {
      if (swiper.autoplay && swiper.autoplay.running) swiper.autoplay.stop();
    };
    const focusOutHandler = (event) => {
      if (swiper.el.contains(event.relatedTarget)) return;
      if (swiper.autoplay) swiper.autoplay.start();
    };

    swiper.el.addEventListener("mouseenter", mouseEnterHandler);
    swiper.el.addEventListener("mouseleave", mouseLeaveHandler);
    swiper.el.addEventListener("focusin", focusInHandler);
    swiper.el.addEventListener("focusout", focusOutHandler);
    swiper.on("touchStart", touchStartHandler);
    swiper.on("touchEnd", touchEndHandler);

    swiper._customHandlers = {
      mouseEnterHandler,
      mouseLeaveHandler,
      focusInHandler,
      focusOutHandler,
      touchStartHandler,
      touchEndHandler,
    };
  };

  // Drag-hint pill that trails the cursor over the slider, appears while the pointer moves and
  // fades out once it stops. Pointer-only and purely a hint (the pill never catches events).
  // Returns a teardown to detach on destroy.
  const initCursor = (root, slider) => {
    if (root.dataset.cursorArrows !== "true" || !pointerFine) return null;

    const cursorEl = root.querySelector(".featured-products__cursor");
    if (!cursorEl) return null;

    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let rafId = null;
    let idleTimer = null;
    let pressed = false; // pointer button held (drag) — keep following, don't hide over cards

    const place = () => {
      cursorEl.style.translate = `${currentX}px ${currentY}px`;
    };

    const updateTarget = (event) => {
      const rect = slider.getBoundingClientRect();
      const w = cursorEl.offsetWidth;
      const h = cursorEl.offsetHeight;
      const x = event.clientX - rect.left - w / 2;
      const y = event.clientY - rect.top - h / 2;
      // Keep the pill fully inside the slider.
      targetX = Math.max(0, Math.min(x, rect.width - w));
      targetY = Math.max(0, Math.min(y, rect.height - h));
    };

    const tick = () => {
      currentX += (targetX - currentX) * CURSOR_EASE;
      currentY += (targetY - currentY) * CURSOR_EASE;
      place();
      rafId = requestAnimationFrame(tick);
    };

    const stopLoop = () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const hide = () => cursorEl.classList.remove("is-visible");

    // Mouse only — pointer events also fire for touch/pen, which shouldn't trigger the hint.
    const isMouse = (event) =>
      !event.pointerType || event.pointerType === "mouse";

    const onEnter = (event) => {
      if (!isMouse(event)) return;
      updateTarget(event);
      // Snap to the cursor on appearance — the trailing weight only applies to movement.
      currentX = targetX;
      currentY = targetY;
      place();
      if (!prefersReducedMotion) {
        stopLoop();
        rafId = requestAnimationFrame(tick);
      }
    };

    const onMove = (event) => {
      if (!isMouse(event)) return;

      // Hide the hint over the product card itself — that's the precise click target.
      // Skipped while dragging so the hint keeps following the held pointer.
      if (!pressed && event.target.closest(".card-horizontal")) {
        hide();
        clearTimeout(idleTimer);
        return;
      }

      updateTarget(event);
      if (prefersReducedMotion) {
        currentX = targetX;
        currentY = targetY;
        place();
      }
      cursorEl.classList.add("is-visible");
      clearTimeout(idleTimer);
      idleTimer = setTimeout(hide, CURSOR_IDLE_MS);
    };

    const onLeave = (event) => {
      if (event && !isMouse(event)) return;
      hide();
      clearTimeout(idleTimer);
      stopLoop();
    };

    const onDown = (event) => {
      if (isMouse(event)) pressed = true;
    };
    const onUp = () => {
      pressed = false;
    };

    // Pointer events (not mouse events) so the hint keeps following while the button is held (drag).
    slider.addEventListener("pointerenter", onEnter);
    slider.addEventListener("pointermove", onMove);
    slider.addEventListener("pointerleave", onLeave);
    slider.addEventListener("pointerdown", onDown);
    // Release can land outside the slider (or be captured), so listen on the window.
    window.addEventListener("pointerup", onUp);

    return () => {
      slider.removeEventListener("pointerenter", onEnter);
      slider.removeEventListener("pointermove", onMove);
      slider.removeEventListener("pointerleave", onLeave);
      slider.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
      clearTimeout(idleTimer);
      stopLoop();
      hide();
    };
  };

  const initSlider = (root, slider) => {
    const wrapper = slider.querySelector(".featured-products__track");
    if (!wrapper || typeof Swiper === "undefined") return;

    const cardsPerRow = parseInt(root.dataset.cardsPerRow, 10) || 3;
    const isLoop = root.dataset.loop === "true";
    const isAutoplay =
      root.dataset.autoplay === "true" && !prefersReducedMotion;
    const duration = parseFloat(root.dataset.autoplayDuration) || 5;
    const marqueeSpeed = Math.max(duration * 2000, 1000);

    buildSlides(wrapper, isLoop, cardsPerRow);

    const swiper = new Swiper(slider, {
      slidesPerView: 1 + PEEK,
      spaceBetween: SPACE_BETWEEN,
      speed: isAutoplay ? marqueeSpeed : INTERACTION_SPEED,
      loop: isLoop,
      allowTouchMove: true,
      watchSlidesProgress: true,
      // Round slide widths / translate to whole px so flush (gap 0) cards don't show a sub-pixel
      // seam between them during the marquee transform.
      roundLengths: true,
      preventClicks: true,
      mousewheel: { forceToAxis: true },
      breakpoints: buildBreakpoints(cardsPerRow),
      autoplay: isAutoplay
        ? {
            delay: 0,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
            waitForTransition: true,
          }
        : false,
      on: {
        init(sw) {
          if (isAutoplay) {
            // Constant-speed belt: linear timing for every marquee step.
            sw.wrapperEl.style.transitionTimingFunction = "linear";
            attachMarqueeControls(sw, marqueeSpeed);
          }
        },
        beforeDestroy(sw) {
          if (sw._cursorTeardown) sw._cursorTeardown();
          if (sw._customHandlers) {
            sw.el.removeEventListener(
              "mouseenter",
              sw._customHandlers.mouseEnterHandler
            );
            sw.el.removeEventListener(
              "mouseleave",
              sw._customHandlers.mouseLeaveHandler
            );
            sw.el.removeEventListener(
              "focusin",
              sw._customHandlers.focusInHandler
            );
            sw.el.removeEventListener(
              "focusout",
              sw._customHandlers.focusOutHandler
            );
          }
          if (sw.autoplay) {
            sw.autoplay.stop();
          }
        },
      },
    });

    swiper._cursorTeardown = initCursor(root, slider);
    instances.set(slider, swiper);
  };

  const destroySlider = (root, slider) => {
    const wrapper = slider.querySelector(".featured-products__track");
    const swiper = instances.get(slider);

    if (swiper) {
      // Listener cleanup happens in the swiper `beforeDestroy` hook.
      try {
        swiper.destroy(true, true);
      } catch (e) {
        /* noop */
      }
      instances.delete(slider);
    }

    if (wrapper) {
      wrapper.style.transform = "";
      wrapper.style.transitionDuration = "";
      wrapper.style.transitionTimingFunction = "";
      restoreOriginals(wrapper);
    }
  };

  const applyLayout = (root) => {
    const slider = root.querySelector(".featured-products__slider");
    if (!slider) return;

    if (getActiveLayout(root) === "slider") {
      if (!instances.has(slider)) initSlider(root, slider);
    } else if (instances.has(slider)) {
      destroySlider(root, slider);
    }
  };

  const initSection = (sectionEl) => {
    if (
      !sectionEl ||
      !sectionEl.classList.contains("featured-products-section")
    ) {
      return;
    }
    const root = sectionEl.querySelector(".featured-products");
    if (root) applyLayout(root);
  };

  // Initialize the section this script instance lives in. Covers sections present at first
  // paint; on a theme-editor re-execution it re-inits the freshly loaded section (idempotent
  // via the shared `instances` map, so it's harmless if the listener below also fires).
  initSection(document.currentScript?.parentElement);

  // Bind the document/window listeners exactly once. They close over the first execution's
  // functions, but those operate on the shared `window.__featuredProducts` state above, so they
  // always act on current sections — never a stale per-execution WeakMap. Binding them on every
  // re-execution would accumulate duplicate listeners that double-init Swiper on reloaded blocks.
  if (!window.__featuredProductsBound) {
    window.__featuredProductsBound = true;

    // resize re-evaluates every section's layout (init/destroy only on a slider <-> grid boundary
    // cross; slidesPerView within slider mode is handled by Swiper).
    let raf;
    window.addEventListener("resize", () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        document
          .querySelectorAll(".featured-products-section .featured-products")
          .forEach(applyLayout);
      });
    });

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      const root = event.target?.querySelector?.(".featured-products");
      const slider = root?.querySelector(".featured-products__slider");
      if (slider && instances.has(slider)) destroySlider(root, slider);
    });
  }
})();
